import React, { useEffect, useState, useCallback } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import AdminLayout from '../../components/AdminLayout'
import { supabase } from '../../lib/supabase'
import { useCanEdit } from '../../lib/useCanEdit'
import {
  getFichaCliente, refundCobro, suscripcionAccion, cambiarKit,
  aplicarDescuento, resetSeguridad, marcarTest, getKitsActivos,
} from '../../lib/fichaClienteApi'

// Sub-sprint A · Parte 2 — Ficha 360 del cliente B2C (dark-on-brand).
// Acciones reales cableadas a las edge functions/RPCs de la Parte 3. Las piezas aún
// no construidas (CFE, NC, producto suelto, editar datos, etc.) van deshabilitadas.

const fmtMoney = (n) => '$' + Math.ceil(Number(n || 0)).toLocaleString('es-UY')
const fmtFecha = (d) => {
  if (!d) return '—'
  // Fechas date-only (YYYY-MM-DD) → parsear a mediodía local para no correr el día por timezone
  const dt = /^\d{4}-\d{2}-\d{2}$/.test(String(d)) ? new Date(d + 'T12:00:00') : new Date(d)
  return dt.toLocaleDateString('es-UY', { day: '2-digit', month: 'short', year: 'numeric' })
}
const fmtFechaHora = (d) => d ? new Date(d).toLocaleString('es-UY', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : '—'
const iniciales = (n) => n ? (n.trim().split(/\s+/).map(p => p[0]).slice(0, 2).join('')).toUpperCase() : '?'
const telE164 = (p) => p ? '598' + String(p).replace(/\D/g, '').replace(/^0+/, '').replace(/^598/, '') : ''

const ESTADO_BADGE = {
  activa: 'bg-green', pausada: 'bg-amber', pausada_por_pago: 'bg-amber',
  pendiente_tarjeta: 'bg-amber', cancelada: 'bg-red',
  pagado: 'bg-green', pendiente: 'bg-muted', fallido: 'bg-red', anulado_por_cancelacion: 'bg-red',
  programada: 'bg-teal', entregada: 'bg-green', en_ruta: 'bg-teal', cancelada_e: 'bg-red',
}
// Etiquetas humanas para estados con nombre técnico feo.
const ESTADO_LABEL = {
  anulado_por_cancelacion: 'Reembolsado',
  pausada_por_pago: 'Pausado (pago)',
}

export default function ClienteDetalle() {
  const { id } = useParams()
  const nav = useNavigate()
  const canEdit = useCanEdit()   // superadmin
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [modal, setModal] = useState(null)     // config del modal de acción
  const [busy, setBusy] = useState(false)
  const [toast, setToast] = useState(null)
  const [kits, setKits] = useState([])
  const [facturas, setFacturas] = useState([])

  const load = useCallback(async () => {
    setLoading(true); setError(null)
    try {
      const d = await getFichaCliente(id)
      if (d?.error) throw new Error(d.error)
      setData(d)
    } catch (e) { setError(e.message) } finally { setLoading(false) }
  }, [id])
  useEffect(() => { load() }, [load])
  useEffect(() => { getKitsActivos().then(setKits).catch(() => {}) }, [])
  // CFE/NC del cliente para el sidebar Facturación (la ficha RPC no los trae).
  useEffect(() => {
    const ids = (data?.suscripciones || []).map(s => s.id).filter(Boolean)
    if (!ids.length) { setFacturas([]); return }
    supabase.from('facturas_emitidas')
      .select('id, tipo_cfe, serie, numero, estado, pdf_path, biller_cfe_id, factura_original_id, created_at')
      .in('cliente_id', ids).order('created_at', { ascending: false })
      .then(({ data: f }) => setFacturas(f || []))
  }, [data])

  function notify(msg, ok = true) { setToast({ msg, ok }); setTimeout(() => setToast(null), 4000) }

  // El bucket facturas-emitidas es privado (datos fiscales) → signed URL temporal, no público.
  async function abrirPdfFactura(path) {
    const { data, error } = await supabase.storage.from('facturas-emitidas').createSignedUrl(path, 120)
    if (error || !data?.signedUrl) return notify('No se pudo abrir el PDF', false)
    window.open(data.signedUrl, '_blank', 'noopener,noreferrer')
  }

  async function correr(fn) {
    setBusy(true)
    try { await fn(); setModal(null); await load(); notify('Acción aplicada ✓') }
    catch (e) { notify(e.message || 'Error', false) }
    finally { setBusy(false) }
  }

  if (loading) return <AdminLayout><div className="ficha360"><div className="f-loading">Cargando ficha…</div></div></AdminLayout>
  if (error) return <AdminLayout><div className="ficha360"><div className="f-error">⚠ {error}</div><Link className="f-back" to="/admin/clientes/suscriptores">← Volver</Link></div></AdminLayout>

  const { profile, stats, suscripciones = [], cobros = [], entregas = [], audit = [] } = data
  const principal = suscripciones.find(s => s.estado === 'activa') || suscripciones[0] || null
  const ultimoCobroAprobado = cobros.find(c => c.mp_status === 'approved')
  const tel = telE164(profile.phone)
  const estadoPill = principal?.estado === 'activa' ? { cls: 'green', txt: 'Activo' }
    : principal && ['pausada', 'pausada_por_pago'].includes(principal.estado) ? { cls: 'amber', txt: 'Pausado' }
    : principal?.estado === 'cancelada' ? { cls: 'red', txt: 'Cancelado' }
    : { cls: 'gray', txt: 'Sin suscripción' }

  // Próximo cobro: usa clientes.fecha_proximo_cobro; si es null (bug backend previo), estima
  // desde el último cobro aprobado + 30 días (marcado "estimado").
  const proxCobro = (s) => {
    if (s.fecha_proximo_cobro) return { fecha: s.fecha_proximo_cobro, est: false }
    const ult = cobros.filter(c => c.cliente_id === s.id && c.mp_status === 'approved' && c.pagado_at)
      .sort((a, b) => new Date(b.pagado_at) - new Date(a.pagado_at))[0]
    if (!ult) return { fecha: null, est: false }
    const d = new Date(ult.pagado_at); d.setDate(d.getDate() + 30)
    return { fecha: d.toISOString().slice(0, 10), est: true }
  }

  // ---- Acciones (abren modal) ----
  const gate = (fn) => canEdit ? fn : () => notify('Solo superadmin puede modificar', false)

  // Refund por API bloqueado (2026-07-09): MP devuelve 401 "Unauthorized use of live
  // credentials" en /payments/{id}/refunds aún con el token dueño del collector, porque la app
  // Suscripciones no tiene activado el producto Pagos. Workaround: reembolso manual desde el panel.
  const REFUND_TOOLTIP =
    'El refund por API está bloqueado (401 scope MP).\n' +
    'Reembolsá manual desde: mercadopago.com.uy → Actividad → Reembolsar.\n' +
    "Pendiente: Roberto contable gestiona activación del producto 'Pagos'\n" +
    'en la app Suscripciones MP (panel developer o soporte MP).'
  const abrirPanelRefundMP = () => window.open('https://mercadopago.com.uy/activities', '_blank', 'noopener,noreferrer')

  // PARKED: accionRefund/refundCobro — reactivar cuando MP active el producto Pagos en la app
  // Suscripciones (ver [[mp_arquitectura]] / PENDIENTES [MP][BLOQUEANTE-REFUNDS]).
  // eslint-disable-next-line no-unused-vars
  const accionRefund = (cobro) => setModal({
    titulo: `Reembolsar cobro · ${fmtMoney(cobro.monto)}`,
    campos: [
      { name: 'parcial', label: 'Monto a reembolsar (vacío = total)', type: 'money', placeholder: String(cobro.monto) },
      { name: 'motivo', label: 'Motivo', type: 'text', required: true },
    ],
    peligro: true,
    confirmar: (v) => correr(() => refundCobro({
      payment_id: cobro.mp_payment_id, app: 'suscripciones', cliente_id: cobro.cliente_id,
      amount: v.parcial ? Number(v.parcial) : undefined, motivo: v.motivo,
    })),
  })
  const accionCambiarKit = (susc) => setModal({
    titulo: 'Cambiar kit',
    campos: [
      { name: 'nuevo_kit_codigo', label: 'Nuevo kit', type: 'select', required: true,
        options: kits.map(k => ({ value: k.codigo, label: `${k.nombre} · ${fmtMoney(k.precio_susc)}` })) },
      { name: 'motivo', label: 'Motivo', type: 'text', required: true },
    ],
    confirmar: (v) => correr(() => cambiarKit({ cliente_id: susc.id, nuevo_kit_codigo: v.nuevo_kit_codigo, motivo: v.motivo })),
  })
  const accionDescuento = (susc) => setModal({
    titulo: 'Aplicar descuento',
    campos: [
      { name: 'tipo', label: 'Tipo', type: 'select', required: true, options: [{ value: 'porcentaje', label: 'Porcentaje (%)' }, { value: 'monto_fijo', label: 'Monto fijo ($)' }] },
      { name: 'valor', label: 'Valor', type: 'number', required: true },
      { name: 'meses', label: 'Meses', type: 'number', placeholder: '1' },
      { name: 'motivo', label: 'Motivo', type: 'text', required: true },
    ],
    confirmar: (v) => correr(() => aplicarDescuento({ cliente_id: susc.id, tipo: v.tipo, valor: Number(v.valor), meses: Number(v.meses || 1), motivo: v.motivo })),
  })
  const accionPausar = (susc) => setModal({
    titulo: 'Pausar suscripción', peligro: true,
    campos: [{ name: 'meses', label: 'Meses de pausa', type: 'number', placeholder: '1' }, { name: 'motivo', label: 'Motivo', type: 'text', required: true }],
    confirmar: (v) => correr(() => suscripcionAccion({ cliente_id: susc.id, accion: 'pausar', meses: Number(v.meses || 1), motivo: v.motivo })),
  })
  const accionSimple = (susc, accion, titulo, peligro) => setModal({
    titulo, peligro,
    campos: [{ name: 'motivo', label: 'Motivo', type: 'text', required: true }],
    confirmar: (v) => correr(() => suscripcionAccion({ cliente_id: susc.id, accion, motivo: v.motivo })),
  })
  const accionReset = () => setModal({
    titulo: 'Reset total de seguridad', peligro: true,
    aviso: 'Revoca la tarjeta guardada + fuerza cambio de contraseña (email de recovery). Dispositivos y sesiones quedan pendientes (v1).',
    campos: [{ name: 'motivo', label: 'Motivo', type: 'text', required: true }],
    confirmar: (v) => correr(() => resetSeguridad({ profile_id: profile.id, motivo: v.motivo })),
  })
  const accionMarcarTest = () => setModal({
    titulo: profile.es_test ? 'Quitar marca de cuenta de prueba' : 'Marcar como cuenta de prueba',
    campos: [{ name: 'motivo', label: 'Motivo', type: 'text', required: true }],
    confirmar: (v) => correr(() => marcarTest({ profile_id: profile.id, es_test: !profile.es_test, motivo: v.motivo })),
  })

  const Disabled = ({ children }) => <button className="f-act" disabled title="Próximamente">{children}</button>

  return (
    <AdminLayout>
      <style>{FICHA_CSS}</style>
      <div className="ficha360">
        <div className="f-breadcrumb">
          <Link to="/admin/clientes/suscriptores">← Suscriptores</Link>
          <span className="sep">›</span><span>{profile.nombre}</span>
        </div>

        {/* HEADER */}
        <div className="f-header">
          <div className={`f-avatar av-${estadoPill.cls}`}>{iniciales(profile.nombre)}</div>
          <div className="f-head-main">
            <div className="f-name-row">
              <div className="f-name">{profile.nombre || 'Sin nombre'}</div>
              <span className={`f-status s-${estadoPill.cls}`}>{estadoPill.cls === 'green' && <span className="f-dot" />}{estadoPill.txt}</span>
              {profile.es_test && <span className="f-badge bg-red">TEST</span>}
            </div>
            <div className="f-meta">
              Cliente desde {fmtFecha(profile.created_at)}
              {profile.documento && <> · {profile.documento_tipo?.toUpperCase() || 'DOC'} {profile.documento}</>}
              {' · '}ID <code>{profile.id.slice(0, 8)}…</code>
            </div>
            <div className="f-contact">
              <span>✉ {profile.email}</span>
              {tel && <span>💬 {profile.phone}</span>}
              {principal?.direccion && <span>📍 {principal.direccion}{principal.barrio ? ` · ${principal.barrio}` : ''}</span>}
            </div>
          </div>
          <div className="f-quickstats">
            <div title="Facturado neto = CFE emitidos − notas de crédito. Puede ser $0 si todo lo emitido fue anulado con NC (ej. reembolsos), sin que signifique que nunca se facturó."><div className="f-stat-v">{fmtMoney(stats.facturado)}</div><div className="f-stat-l">Facturado ⓘ</div></div>
            <div><div className="f-stat-v">{stats.cobros}</div><div className="f-stat-l">Cobros</div></div>
            <div><div className="f-stat-v">{stats.entregas}</div><div className="f-stat-l">Entregas</div></div>
          </div>
        </div>

        {/* QUICK ACTIONS */}
        <div className="f-quickbar">
          <span className="f-qlabel">Acciones rápidas</span>
          {tel && <Link className="f-act primary" to={`/admin/crm?phone=${tel}`}>💬 Escribirle</Link>}
          <button className="f-act" title={REFUND_TOOLTIP} onClick={abrirPanelRefundMP}>↗ Reembolsar <span style={{ opacity: .7, fontSize: '.85em' }}>(via panel MP)</span></button>
          {principal && <button className="f-act" onClick={gate(() => accionCambiarKit(principal))}>📦 Cambiar kit</button>}
          {principal && <button className="f-act" onClick={gate(() => accionDescuento(principal))}>％ Aplicar descuento</button>}
          {principal && principal.estado === 'activa' && <button className="f-act warn" onClick={gate(() => accionPausar(principal))}>⏸ Pausar</button>}
        </div>

        <div className="f-grid">
          <div className="f-main">
            {/* SUSCRIPCIONES */}
            {suscripciones.length === 0 && <div className="f-card"><div className="f-empty">Sin suscripciones — cuenta creada sin activar.</div></div>}
            {suscripciones.map(s => (
              <div className="f-card" key={s.id}>
                <div className="f-card-head">
                  <div className="f-card-title">📦 Suscripción <span className={`f-badge ${ESTADO_BADGE[s.estado] || 'bg-muted'}`}>{s.estado}</span></div>
                </div>
                <div className="f-kv">
                  <div className="k">Kit</div><div className="v"><b>{s.kit_nombre || s.kit_actual || '—'}</b>{s.kit_precio ? ` · ${fmtMoney(s.kit_precio)}/mes` : ''}</div>
                  <div className="a"><button className="f-mini" onClick={gate(() => accionCambiarKit(s))}>Cambiar</button></div>
                  <div className="k">Próximo cobro</div><div className="v">{(() => { const p = proxCobro(s); return p.fecha ? fmtFecha(p.fecha) + (p.est ? ' · estimado' : '') : '—' })()}</div>
                  <div className="a"><button className="f-mini" disabled title="Próximamente">Cambiar fecha</button></div>
                  <div className="k">Método de pago</div><div className="v">{s.mp_card_brand ? `${s.mp_card_brand}${s.mp_card_last4 ? ` ···${s.mp_card_last4}` : ''}` : '—'}</div>
                  <div className="a"><button className="f-mini" disabled title="Próximamente">Enviar link cambiar</button></div>
                  <div className="k">Preapproval MP</div><div className="v"><code>{s.mp_preapproval_id ? s.mp_preapproval_id.slice(0, 10) + '…' : '—'}</code> {s.mp_preapproval_status}</div>
                  <div className="a"></div>
                </div>
                <div className="f-card-actions">
                  {s.estado === 'activa' && <button className="f-act warn sm" onClick={gate(() => accionPausar(s))}>⏸ Pausar</button>}
                  {['pausada', 'pausada_por_pago'].includes(s.estado) && <button className="f-act sm" onClick={gate(() => accionSimple(s, 'reactivar', 'Reactivar suscripción', false))}>▶ Reactivar</button>}
                  {s.estado !== 'cancelada' && <button className="f-act danger sm" onClick={gate(() => accionSimple(s, 'cancelar', 'Cancelar suscripción', true))}>✕ Cancelar</button>}
                </div>
              </div>
            ))}

            {/* COBROS */}
            <div className="f-card">
              <div className="f-card-head"><div className="f-card-title">💳 Cobros</div></div>
              {cobros.length === 0 ? <div className="f-empty">Sin cobros.</div> : (
                <div className="f-mini-table">
                  <div className="f-mrow head"><div>Fecha</div><div>Payment ID</div><div>Monto</div><div>Estado</div><div></div></div>
                  {cobros.map(c => (
                    <div className="f-mrow" key={c.id}>
                      <div>{fmtFecha(c.pagado_at || c.created_at)}</div>
                      <div>{c.mp_payment_id ? <a href={`https://www.mercadopago.com.uy/activities/detail/${c.mp_payment_id}`} target="_blank" rel="noopener noreferrer" title="Ver en MercadoPago"><code>{c.mp_payment_id}</code></a> : '—'}</div>
                      <div>{fmtMoney(c.monto)}</div>
                      <div><span className={`f-badge ${ESTADO_BADGE[c.estado] || 'bg-muted'}`} title={c.estado}>{ESTADO_LABEL[c.estado] || c.estado}</span></div>
                      <div>{c.mp_status === 'approved' && c.mp_payment_id && <button className="f-mini" title={REFUND_TOOLTIP} onClick={abrirPanelRefundMP}>↗ Refund</button>}</div>
                    </div>
                  ))}
                </div>
              )}
              <div className="f-note">Refund: por ahora manual desde el panel MP (API bloqueada · 401 scope app Suscripciones) · Ver detalle / CFE / NC — próximamente.</div>
            </div>

            {/* ENTREGAS */}
            <div className="f-card">
              <div className="f-card-head"><div className="f-card-title">🚚 Entregas</div></div>
              {entregas.length === 0 ? <div className="f-empty">Sin entregas.</div> : (
                <div className="f-mini-table">
                  <div className="f-mrow e head"><div>Fecha</div><div>Kit</div><div>Estado</div><div></div></div>
                  {entregas.map(e => (
                    <div className="f-mrow e" key={e.id}>
                      <div>{fmtFecha(e.fecha_programada)}</div>
                      <div>{e.kit_codigo || '—'}</div>
                      <div><span className={`f-badge ${ESTADO_BADGE[e.estado] || 'bg-muted'}`}>{e.estado}</span></div>
                      <div><button className="f-mini" disabled title="Próximamente">⋯</button></div>
                    </div>
                  ))}
                </div>
              )}
              <div className="f-note">Cambiar día / dirección / producto suelto / marcar manual — próximamente.</div>
            </div>

            {/* COMUNICACIÓN */}
            <div className="f-card">
              <div className="f-card-head"><div className="f-card-title">💬 Comunicación</div>
                {tel && <Link className="f-mini" to={`/admin/crm?phone=${tel}`}>Ver en CRM</Link>}</div>
              <div className="f-empty">Abrí la conversación completa en el CRM interno {tel ? <Link to={`/admin/crm?phone=${tel}`}>(WhatsApp de {profile.phone})</Link> : ''}. Preview inline — próximamente.</div>
            </div>

            {/* SEGURIDAD */}
            <div className="f-card">
              <div className="f-card-head"><div className="f-card-title">🔒 Seguridad</div>
                <button className="f-act danger sm" onClick={gate(accionReset)}>⚠ Reset total</button></div>
              <div className="f-empty">Dispositivos confiables: sin tabla `user_devices` todavía (pendiente). El reset revoca tarjeta + fuerza cambio de contraseña.</div>
            </div>
          </div>

          {/* SIDEBAR */}
          <div className="f-side">
            <div className="f-side-card">
              <div className="f-side-title">Facturación</div>
              {facturas.length === 0 ? (
                <Disabled>Sin CFE emitidos</Disabled>
              ) : facturas.map(f => {
                const tipoLabel = f.tipo_cfe === 'e_nota_credito' ? '➖ NC' : f.tipo_cfe === 'e_factura' ? '🧾 e-Factura' : f.tipo_cfe === 'e_ticket' ? '🧾 e-Ticket' : f.tipo_cfe
                return (
                  <div key={f.id} className="f-side-item" style={{ cursor: 'default', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
                    <span>{tipoLabel} {f.serie}-{f.numero}{f.estado === 'anulada' && <span className="f-badge bg-red" style={{ fontSize: 8.5, marginLeft: 5 }}>anulada</span>}</span>
                    {f.pdf_path
                      ? <button onClick={() => abrirPdfFactura(f.pdf_path)} style={{ background: 'none', border: 'none', padding: 0, color: 'var(--teal-hi)', fontSize: 11, cursor: 'pointer', flexShrink: 0, fontFamily: 'inherit' }}>PDF ↗</button>
                      : <span style={{ fontSize: 9.5, color: 'var(--ink4)', flexShrink: 0 }} title="Biller CFE ID">#{f.biller_cfe_id}</span>}
                  </div>
                )
              })}
            </div>
            <div className="f-side-card">
              <div className="f-side-title">Promos y descuentos</div>
              {principal ? <button className="f-side-item" onClick={gate(() => accionDescuento(principal))}>％ Aplicar descuento</button> : <Disabled>％ Aplicar descuento</Disabled>}
              <Disabled>🎁 Regalo próxima entrega</Disabled>
            </div>
            <div className="f-side-card">
              <div className="f-side-title">Perfil</div>
              <label className="f-check">
                <input type="checkbox" checked={!!profile.es_test} onChange={gate(accionMarcarTest)} disabled={!canEdit} />
                Cuenta de prueba (es_test)
              </label>
              <Disabled>✎ Editar datos</Disabled>
              <Disabled>📍 Cambiar dirección</Disabled>
            </div>
            <div className="f-side-card danger">
              <div className="f-side-title">⚠ Zona peligrosa</div>
              {principal && principal.estado === 'activa' && <button className="f-side-item warn" onClick={gate(() => accionPausar(principal))}>⏸ Pausar suscripción</button>}
              {principal && principal.estado !== 'cancelada' && <button className="f-side-item danger" onClick={gate(() => accionSimple(principal, 'cancelar', 'Cancelar suscripción', true))}>✕ Cancelar suscripción</button>}
              <button className="f-side-item danger" onClick={gate(accionReset)}>🔒 Reset seguridad</button>
              <Disabled>🗑 Eliminar definitivamente</Disabled>
              <div className="f-side-note">Toda acción peligrosa pide motivo y queda en el audit trail.</div>
            </div>
          </div>
        </div>

        {/* AUDIT */}
        <div className="f-card">
          <div className="f-card-head"><div className="f-card-title">🕓 Registro de actividad admin</div></div>
          {audit.length === 0 ? <div className="f-empty">Sin acciones registradas.</div> : (
            <div className="f-audit">
              {audit.map((a, i) => (
                <div className="f-audit-item" key={i}>
                  <div className="f-audit-ic">•</div>
                  <div><div className="f-audit-t">{a.action}{a.motivo ? ` — ${a.motivo}` : ''}</div>
                    <div className="f-audit-m">{a.admin} · {fmtFechaHora(a.created_at)}</div></div>
                </div>
              ))}
            </div>
          )}
        </div>

        {!canEdit && <div className="f-readonly">👁️ Modo lectura — solo superadmin puede ejecutar acciones.</div>}

        {/* MODAL + toast DENTRO de .ficha360 para que aplique el CSS scopeado (fixed overlay centrado) */}
        {modal && <AccionModal config={modal} busy={busy} onClose={() => setModal(null)} />}
        {toast && <div className={`f-toast ${toast.ok ? 'ok' : 'err'}`}>{toast.msg}</div>}
      </div>
    </AdminLayout>
  )
}

function AccionModal({ config, busy, onClose }) {
  const [vals, setVals] = useState({})
  const faltan = (config.campos || []).some(c => c.required && !String(vals[c.name] || '').trim())
  return (
    <div className="f-modal-bg" onClick={onClose}>
      <div className="f-modal" onClick={e => e.stopPropagation()}>
        <div className={`f-modal-title ${config.peligro ? 'peligro' : ''}`}>{config.titulo}</div>
        {config.aviso && <div className="f-modal-aviso">{config.aviso}</div>}
        {(config.campos || []).map(c => (
          <label className="f-field" key={c.name}>
            <span>{c.label}{c.required && ' *'}</span>
            {c.type === 'select' ? (
              <select value={vals[c.name] || ''} onChange={e => setVals(v => ({ ...v, [c.name]: e.target.value }))}>
                <option value="">Elegir…</option>
                {c.options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            ) : (
              <input type={c.type === 'number' || c.type === 'money' ? 'number' : 'text'}
                placeholder={c.placeholder || ''} value={vals[c.name] || ''}
                onChange={e => setVals(v => ({ ...v, [c.name]: e.target.value }))} />
            )}
          </label>
        ))}
        <div className="f-modal-actions">
          <button className="f-act" onClick={onClose} disabled={busy}>Cancelar</button>
          <button className={`f-act ${config.peligro ? 'danger' : 'primary'}`} disabled={busy || faltan} onClick={() => config.confirmar(vals)}>
            {busy ? 'Aplicando…' : 'Confirmar'}
          </button>
        </div>
      </div>
    </div>
  )
}

const FICHA_CSS = `
.ficha360 {
  --nv:#1A3461; --bg:#F8FAFC; --teal:#00A8B5; --teal-hi:#008E99;
  --green:#16A34A; --green-bg:#DCFCE7; --sky:#0EA5E9; --sky-bg:#E0F2FE; --red:#DC2626; --red-bg:#FEE2E2; --amber:#CA8A04; --amber-bg:#FEF9C3;
  --ink1:#1A3461; --ink2:#475569; --ink3:#64748B; --ink4:#94A3B8;
  --line:#E2E8F0; --line-h:#E2E8F0; --surf:#F8FAFC; --surf-h:#F1F5F9;
  --card-sh:0 1px 3px rgba(15,31,61,.05);
  color:var(--ink1); font-size:13.5px;
}
.ficha360 code { font-family:'SF Mono',Monaco,monospace; font-size:11px; color:var(--teal-hi); }
.ficha360 .f-loading,.ficha360 .f-error { padding:40px; text-align:center; color:var(--ink3); }
.ficha360 .f-error { color:var(--red); } .ficha360 .f-back,.ficha360 .f-breadcrumb a { color:var(--teal-hi); text-decoration:none; }
.ficha360 .f-breadcrumb { font-size:12px; color:var(--ink3); margin-bottom:12px; } .ficha360 .f-breadcrumb .sep { margin:0 8px; }

.ficha360 .f-header { background:#fff; border:1px solid var(--line); border-radius:14px; padding:22px; margin-bottom:16px; display:grid; grid-template-columns:auto 1fr auto; gap:20px; align-items:start; box-shadow:var(--card-sh); }
.ficha360 .f-avatar { width:64px; height:64px; border-radius:50%; display:flex; align-items:center; justify-content:center; font-size:22px; font-weight:700; flex-shrink:0; }
.av-green{background:var(--green-bg);color:var(--green)} .av-amber{background:var(--amber-bg);color:var(--amber)} .av-red{background:var(--red-bg);color:var(--red)} .av-gray{background:#F1F5F9;color:var(--ink2)}
.ficha360 .f-name-row { display:flex; align-items:center; gap:10px; flex-wrap:wrap; }
.ficha360 .f-name { font-size:20px; font-weight:900; letter-spacing:-.02em; color:var(--nv); }
.ficha360 .f-status { font-size:11px; padding:3px 10px; border-radius:12px; font-weight:600; display:inline-flex; align-items:center; gap:5px; }
.s-green{background:var(--green-bg);color:var(--green)} .s-amber{background:var(--amber-bg);color:var(--amber)} .s-red{background:var(--red-bg);color:var(--red)} .s-gray{background:#F1F5F9;color:var(--ink2)}
.ficha360 .f-dot{width:6px;height:6px;border-radius:50%;background:var(--green);animation:fpulse 2s infinite} @keyframes fpulse{0%,100%{opacity:1}50%{opacity:.4}}
.ficha360 .f-meta { color:var(--ink3); font-size:12px; margin:6px 0 10px; }
.ficha360 .f-contact { display:flex; gap:14px; flex-wrap:wrap; font-size:12px; color:var(--ink2); }
.ficha360 .f-quickstats { display:flex; gap:18px; padding:12px 18px; background:var(--bg); border:1px solid var(--line); border-radius:12px; }
.ficha360 .f-quickstats>div{text-align:center} .ficha360 .f-stat-v{font-size:18px;font-weight:700;color:var(--teal-hi)} .ficha360 .f-stat-l{font-size:9.5px;color:var(--ink3);text-transform:uppercase;letter-spacing:.06em;margin-top:3px}

.ficha360 .f-quickbar { background:#fff; border:1px solid var(--line); border-radius:12px; padding:12px 16px; margin-bottom:18px; display:flex; align-items:center; gap:10px; flex-wrap:wrap; position:sticky; top:12px; z-index:5; box-shadow:var(--card-sh); }
.ficha360 .f-qlabel { font-size:10px; color:var(--ink3); text-transform:uppercase; letter-spacing:.08em; font-weight:600; }
.ficha360 .f-act { background:#fff; color:var(--nv); border:1px solid var(--line-h); border-radius:8px; padding:8px 13px; font-size:12px; font-weight:500; cursor:pointer; font-family:inherit; display:inline-flex; align-items:center; gap:6px; text-decoration:none; transition:all .15s; }
.ficha360 .f-act:hover:not(:disabled){background:var(--surf-h)} .ficha360 .f-act:disabled{opacity:.4;cursor:not-allowed}
.ficha360 .f-act.primary{background:var(--teal);color:#0F1F3D;border-color:var(--teal);font-weight:600} .ficha360 .f-act.primary:hover{background:var(--teal-hi);color:#fff}
.ficha360 .f-act.danger{background:var(--red-bg);color:var(--red);border-color:rgba(220,38,38,.25)} .ficha360 .f-act.warn{background:var(--amber-bg);color:var(--amber);border-color:rgba(202,138,4,.28)}
.ficha360 .f-act.sm{padding:6px 10px;font-size:11px}

.ficha360 .f-grid { display:grid; grid-template-columns:1fr 300px; gap:18px; align-items:start; }
@media (max-width:900px){ .ficha360 .f-grid{grid-template-columns:1fr} }
.ficha360 .f-card { background:#fff; border:1px solid var(--line); border-radius:12px; padding:18px; margin-bottom:16px; box-shadow:var(--card-sh); }
.ficha360 .f-card-head { display:flex; align-items:center; justify-content:space-between; margin-bottom:14px; gap:8px; flex-wrap:wrap; }
.ficha360 .f-card-title { font-size:14px; font-weight:700; color:var(--nv); display:flex; align-items:center; gap:8px; }
.ficha360 .f-card-actions { display:flex; gap:6px; margin-top:14px; flex-wrap:wrap; }
.ficha360 .f-empty { color:var(--ink3); font-size:12.5px; padding:6px 0; } .ficha360 .f-empty a{color:var(--teal-hi)}
.ficha360 .f-note { margin-top:10px; font-size:10.5px; color:var(--ink4); }

.ficha360 .f-kv { display:grid; grid-template-columns:130px 1fr auto; gap:8px 14px; align-items:center; }
.ficha360 .f-kv .k { font-size:10.5px; color:var(--ink3); text-transform:uppercase; letter-spacing:.05em; }
.ficha360 .f-kv .v { font-size:12.5px; } .ficha360 .f-kv .a { text-align:right; }
.ficha360 .f-mini { background:#fff; border:1px solid var(--line-h); color:var(--nv); border-radius:6px; padding:5px 9px; font-size:11px; cursor:pointer; font-family:inherit; text-decoration:none; }
.ficha360 .f-mini:hover:not(:disabled){background:var(--surf-h)} .ficha360 .f-mini:disabled{opacity:.4;cursor:not-allowed}

.ficha360 .f-mini-table { border:1px solid var(--line); border-radius:10px; overflow:hidden; }
.ficha360 .f-mrow { display:grid; grid-template-columns:1fr 1.3fr .7fr .8fr 70px; gap:10px; padding:10px 14px; align-items:center; border-top:1px solid var(--line); font-size:12px; }
.ficha360 .f-mrow.e { grid-template-columns:1fr 1fr .8fr 60px; }
.ficha360 .f-mrow.head { background:var(--bg); border-top:none; font-size:10px; color:var(--ink3); text-transform:uppercase; letter-spacing:.05em; font-weight:600; }
.ficha360 .f-badge { font-size:10.5px; padding:3px 10px; border-radius:999px; font-weight:700; }
.bg-green{background:var(--green-bg);color:var(--green)} .bg-amber{background:var(--amber-bg);color:var(--amber)} .bg-red{background:var(--red-bg);color:var(--red)} .bg-teal{background:var(--sky-bg);color:var(--sky)} .bg-muted{background:#F1F5F9;color:var(--ink3)}

.ficha360 .f-side-card { background:#fff; border:1px solid var(--line); border-radius:12px; padding:16px; margin-bottom:14px; box-shadow:var(--card-sh); }
.ficha360 .f-side-card.danger { background:rgba(226,75,74,.05); border-color:rgba(198,64,63,.25); }
.ficha360 .f-side-title { font-size:10.5px; color:var(--ink3); text-transform:uppercase; letter-spacing:.06em; font-weight:600; margin-bottom:10px; }
.ficha360 .f-side-card.danger .f-side-title { color:var(--red); }
.ficha360 .f-side-item { display:block; width:100%; text-align:left; background:#fff; color:var(--nv); border:1px solid var(--line-h); border-radius:8px; padding:9px 11px; font-size:12px; cursor:pointer; font-family:inherit; margin-bottom:6px; }
.ficha360 .f-side-item:hover{background:var(--surf-h)} .ficha360 .f-side-item.danger{color:var(--red)} .ficha360 .f-side-item.warn{color:var(--amber)}
.ficha360 .f-check { display:flex; align-items:center; gap:8px; font-size:12px; color:var(--ink1); margin-bottom:10px; cursor:pointer; }
.ficha360 .f-side-note { font-size:10px; color:var(--ink4); margin-top:8px; line-height:1.4; }

.ficha360 .f-audit { display:flex; flex-direction:column; gap:8px; }
.ficha360 .f-audit-item { display:flex; gap:10px; align-items:flex-start; background:var(--bg); border:1px solid var(--line); border-radius:8px; padding:9px 12px; }
.ficha360 .f-audit-ic { color:var(--teal); font-size:16px; line-height:1; }
.ficha360 .f-audit-t { font-size:12.5px; } .ficha360 .f-audit-m { font-size:10.5px; color:var(--ink3); margin-top:2px; }
.ficha360 .f-readonly { margin-top:16px; text-align:center; font-size:12px; color:var(--ink3); }

.ficha360 .f-modal-bg { position:fixed; inset:0; background:rgba(15,31,61,.35); display:flex; align-items:center; justify-content:center; z-index:100; padding:20px; }
.ficha360 .f-modal { background:#fff; border:1px solid var(--line-h); border-radius:14px; padding:22px; width:100%; max-width:420px; box-shadow:0 12px 32px rgba(15,31,61,.18); }
.ficha360 .f-modal-title { font-size:16px; font-weight:700; color:var(--nv); margin-bottom:14px; } .ficha360 .f-modal-title.peligro{color:var(--red)}
.ficha360 .f-modal-aviso { font-size:11.5px; color:var(--ink2); background:rgba(226,75,74,.06); border:1px solid rgba(198,64,63,.20); border-radius:8px; padding:10px; margin-bottom:14px; }
.ficha360 .f-field { display:block; margin-bottom:12px; } .ficha360 .f-field span { display:block; font-size:11px; color:var(--ink3); margin-bottom:5px; }
.ficha360 .f-field input,.ficha360 .f-field select { width:100%; background:var(--bg); border:1px solid var(--line-h); color:var(--ink1); border-radius:8px; padding:9px 11px; font-size:13px; font-family:inherit; outline:none; }
.ficha360 .f-field input:focus,.ficha360 .f-field select:focus { border-color:var(--teal); }
.ficha360 .f-modal-actions { display:flex; gap:8px; justify-content:flex-end; margin-top:16px; }
.ficha360 .f-toast { position:fixed; bottom:24px; left:50%; transform:translateX(-50%); background:#fff; border:1px solid var(--line-h); border-radius:10px; padding:12px 18px; font-size:13px; z-index:200; box-shadow:0 8px 24px rgba(15,31,61,.18); }
.ficha360 .f-toast.ok{border-color:rgba(30,158,74,.5);color:var(--green)} .ficha360 .f-toast.err{border-color:rgba(198,64,63,.5);color:var(--red)}
`
